import Button from '@/componentsDS/Button/Button';
import { ButtonType, ButtonVariant } from '@/componentsDS/Button/Button.type';
import Checkbox from '@/componentsDS/Checkbox/Checkbox';
import Icon from '@/componentsDS/Icon/Icon';
import Loader from '@/componentsDS/Loader/Loader';
import Tooltip from "@/componentsDS/Tooltip/Tooltip";
import useEllipsisCheck from "@/hooksDS/use-ellipsis-check/index";
import classNames from 'classnames';
import { isEqual, orderBy } from 'lodash';
import PropTypes from 'prop-types';
import React, { cloneElement, isValidElement, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Manager, Popper, Reference } from 'react-popper';

/* -----------------------------------------------
* HOC for managing dropdown states
* --------------------------------------------- */
const withDropdownManager = (WrappedComponent) => {
	const DropdownManager = (props) => {
		const [internalIsOpen, setInternalIsOpen] = useState(false);

		const dropdownRef = useRef(null);
		const [dropdownWidth, setDropdownWidth] = useState(null);

		const isOpen = props.isOpen !== undefined ? props.isOpen : internalIsOpen;
		const setIsOpen = props.setIsOpen || setInternalIsOpen;

		const closeDropdown = () => setIsOpen(false);

		const toggleDropdown = () => {
			if (!isOpen) {
				document.dispatchEvent(new CustomEvent('dropdownCloseAll', {}));
			}
			setIsOpen(!isOpen);
		};

		useEffect(() => {
			if (dropdownRef.current) {
				const triggerElementWidth = dropdownRef.current.offsetWidth;
				setDropdownWidth(triggerElementWidth);
			}
		}, []);

		useEffect(() => {
			const handleClickOutside = (event) => {
				const dropdownList = document.querySelector('.DS__dropdown-list');

				if ((dropdownRef.current && !dropdownRef.current.contains(event.target))
					&& (dropdownList && !dropdownList.contains(event.target))
				) {
					closeDropdown();
				}
			};

			document.addEventListener('mousedown', handleClickOutside);
			return () => {
				document.removeEventListener('mousedown', handleClickOutside);
			};
		}, []);

		useEffect(() => {
			const closeAllListener = () => closeDropdown();
			document.addEventListener('dropdownCloseAll', closeAllListener);
			return () => {
				document.removeEventListener('dropdownCloseAll', closeAllListener);
			};
		}, []);

		return (
			<div ref={dropdownRef} className="DS__dropdown-wrapper">
				<WrappedComponent {...props} isOpen={isOpen} toggleDropdown={toggleDropdown} dropdownWidth={dropdownWidth} />
			</div>
		);
	};

	DropdownManager.propTypes = {
		isOpen: PropTypes.bool,
		setIsOpen: PropTypes.func
	};

	return DropdownManager;
};

/* -----------------------------------------------
* Helpers
* --------------------------------------------- */
const groupByCategory = (options) =>
	options.reduce(
		(acc, item) => {
			const categoryKey = item.category ?? 'uncategorized';
			if (!acc[categoryKey]) {
				acc[categoryKey] = [];
			}
			acc[categoryKey].push(item);
			return acc;
		},
		{}
	);

const reorderDropdownMultiple = (options, value, setReorderedOptions) => {
	const selectedIds = value.map(val => val.id);

	// Separate categorized and uncategorized options
	const uncategorized = options.filter(opt => !opt.category);
	const categorized = options.filter(opt => opt.category);

	// Sort categorized options by category
	categorized.sort((a, b) => {
		if (a.category < b.category) return -1;
		if (a.category > b.category) return 1;
		return 0;
	});

	// Reorder so that selected options come first in each group
	const reorderBySelection = (opts) => {
		const selected = opts.filter(opt => selectedIds.includes(opt.id));
		const unselected = opts.filter(opt => !selectedIds.includes(opt.id));
		return [...selected, ...unselected];
	};

	const finalOptions = [
		...reorderBySelection(uncategorized), // Unsorted uncategorized at the top
		...reorderBySelection(categorized)     // Sorted categorized options with selection on top
	];

	setReorderedOptions(finalOptions);
};

const reorderDropdownSingle = (options, setReorderedOptions) => {
	setReorderedOptions(options);
};

const handleDropdownClose = (temporarySelection, value, onAdd) => {
	const sortedSelection = orderBy(temporarySelection, ['label'], ['asc']);
	if (JSON.stringify(sortedSelection) !== JSON.stringify(value)) {
		onAdd(sortedSelection);
	}
};

/* -----------------------------------------------
* Child Components
* --------------------------------------------- */
const OptionItem = ({ option, multiple, hasCategory, temporarySelection, handleMultipleSelection, handleSingleSelection, size }) => {
	const isSelected = temporarySelection.some((item) => item.id === option.id);
	const { isEllipsis, elementRefs } = useEllipsisCheck();

	const dropdownItemContentClass = classNames('DS__dropdown-item__content', {
		'DS__dropdown-item__content--with-category': hasCategory
	});

	const dropdownItemClass = classNames('DS__dropdown-item', {
		'DS__dropdown-item--selected': isSelected,
		'DS__dropdown-item--disabled': option.isDisabled
	});

	return (
		<li key={option.id} className={dropdownItemClass}>
			{multiple ? (
				<Button
					type={ButtonType.UTILITY}
					variant={ButtonVariant.UTILITY}
					onClick={(e) => {
						e.stopPropagation();
						handleMultipleSelection(option)
					}}

					text={
						<div className={dropdownItemContentClass}>
							<div className="DS__dropdown-item__content-checkbox">
								<Checkbox id={option.id.toString()} checked={isSelected} onChange={() => handleMultipleSelection(option)} disabled={option.isDisabled} />
							</div>
							<div className="DS__dropdown-item__content-text">
								<Tooltip id={option.label} content={option.label} disabled={!isEllipsis[0]} portal>
									<div className="DS__dropdown-item__label" ref={(el) => (elementRefs.current[0] = el)}>
										{option.label}
									</div>
								</Tooltip>

								{option.subLabel && (
									<Tooltip id={option.subLabel} content={option.subLabel} disabled={!isEllipsis[1]} portal>
										<div className="DS__dropdown-item__sub-label" ref={(el) => (elementRefs.current[1] = el)}>
											{option.subLabel}
										</div>
									</Tooltip>
								)}
							</div>
						</div>
					}
				/>
			) : (
				<Button
					type={ButtonType.UTILITY}
					variant={ButtonVariant.UTILITY}
					onClick={() => handleSingleSelection(option)}
					text={
						<div className={dropdownItemContentClass}>
							<div className="DS__dropdown-item__content-text">
								<Tooltip id={option.label} content={option.label} disabled={!isEllipsis[0]} portal>
									<div className="DS__dropdown-item__label" ref={(el) => (elementRefs.current[0] = el)}>
										{option.label}
									</div>
								</Tooltip>

								{option.subLabel && (
									<Tooltip id={option.subLabel} content={option.subLabel} disabled={!isEllipsis[1]} portal>
										<div className="DS__dropdown-item__sub-label" ref={(el) => (elementRefs.current[1] = el)}>
											{option.subLabel}
										</div>
									</Tooltip>
								)}
							</div>
						</div>
					}
				/>
			)}
		</li>
	);
};

const CategoryGroup = ({ hasCategory, category, groupedOptions, selectByCategory, temporarySelection, size, handleSelectAllInCategory, multiple, handleMultipleSelection, handleSingleSelection }) => (
	<React.Fragment key={category}>
		{category !== 'uncategorized' && (
			<li className="DS__category-label">
				<div>{category}</div>
			</li>
		)}
		{selectByCategory && category !== 'uncategorized' && (
			<li className="DS__dropdown-item">
				<Button
					type={ButtonType.UTILITY}
					variant={ButtonVariant.UTILITY}
					text={
						<div className="DS__dropdown-item__all-option">
							<Checkbox id={`select-all-${category}`} checked={groupedOptions[category].every((item) => temporarySelection.some((selected) => selected.id === item.id))} onChange={() => handleSelectAllInCategory(category)} label={`All ${category}`} />
						</div>
					}
				/>
			</li>
		)}
		{groupedOptions[category].map((option) => (
			<OptionItem key={option.id} option={option} hasCategory={hasCategory} multiple={multiple} temporarySelection={temporarySelection} size={size} handleMultipleSelection={handleMultipleSelection} handleSingleSelection={handleSingleSelection} />
		))}
	</React.Fragment>
);

/* -----------------------------------------------
* Main Dropdown Component
* --------------------------------------------- */
const Dropdown = ({
	options,
	value = [],
	onAdd,
	multiple = false,
	allowSelectAll = false,
	selectByCategory = false,
	trigger,
	isOpen,
	toggleDropdown,
	dropdownWidth,
	setWidth,
	isLoading,
	noResults,
	size = 'default',
	placement = 'bottom-start',
	offset = [0, 2],
	disabled = false,
	filteredOptions,
	portal = false,
	fullWidth = false
}) => {

	const [reorderedOptions, setReorderedOptions] = useState(options);
	const [temporarySelection, setTemporarySelection] = useState(value);

	/* Sync local selection with external value */
	useEffect(() => {
		if (!isEqual(temporarySelection, value)) {
			setTemporarySelection(value);
		}
	}, [value, temporarySelection]);

	/* Group options by category */
	let filteredReordered = reorderedOptions;
	if (filteredOptions && filteredOptions.length > 0) {
		filteredReordered = reorderedOptions.filter((reorderedOption) => filteredOptions.some((option) => option.id === reorderedOption.id));
	}
	const groupedOptions = groupByCategory(filteredReordered);

	const hasCategory = Object.keys(groupedOptions).some((category) => category !== 'uncategorized');

	/* Single selection */
	const handleSingleSelection = (selectedOption) => {
		setTemporarySelection([selectedOption]);
		onAdd([selectedOption]);
		toggleDropdown();
	};

	/* Multiple selection */
	const handleMultipleSelection = (selectedOption) => {
		const isSelected = temporarySelection.some((item) => item.id === selectedOption.id);
		const newSelection = isSelected ? temporarySelection.filter((item) => item.id !== selectedOption.id) : [...temporarySelection, selectedOption];
		setTemporarySelection(newSelection);
		onAdd(newSelection);
	};

	/* Select all */
	const handleSelectAll = () => {
		if (temporarySelection.length === reorderedOptions.length) {
			setTemporarySelection([]);
			onAdd([]);
		} else {
			setTemporarySelection(reorderedOptions);
			onAdd(reorderedOptions);
		}
	};

	/* Select all by category */
	const handleSelectAllInCategory = (category) => {
		const categoryItems = groupedOptions[category];
		const allSelectedInCategory = categoryItems.every((item) => temporarySelection.some((sel) => sel.id === item.id));

		if (allSelectedInCategory) {
			const newValue = temporarySelection.filter((item) => item.category !== category);
			setTemporarySelection(newValue);
			onAdd(newValue);
		} else {
			const newSelections = categoryItems.filter((item) => !temporarySelection.some((sel) => sel.id === item.id));
			const combined = [...temporarySelection, ...newSelections];
			setTemporarySelection(combined);
			onAdd(combined);
		}
	};

	/* Reorder items after dropdown closes */
	useEffect(() => {
		if (!isOpen) {
			if (multiple) {
				reorderDropdownMultiple(options, value, setReorderedOptions);
			} else {
				reorderDropdownSingle(options, setReorderedOptions);
			}
		}
	}, [isOpen, multiple, options, value]);

	/* Finalize selection on close */
	useEffect(() => {
		if (!isOpen) {
			handleDropdownClose(temporarySelection, value, onAdd);
		}
	}, [isOpen, temporarySelection]);

	const isButtonTrigger = () => {
		return isValidElement(trigger) && trigger.type === Button;
	};

	const renderGroupedOptions = () => Object.keys(groupedOptions).map((category) => <CategoryGroup key={category} hasCategory={hasCategory} category={category} groupedOptions={groupedOptions} selectByCategory={selectByCategory} temporarySelection={temporarySelection} size={size} handleSelectAllInCategory={handleSelectAllInCategory} multiple={multiple} handleMultipleSelection={handleMultipleSelection} handleSingleSelection={handleSingleSelection} />);

	const renderReference = ({ ref }) => {
		if (isButtonTrigger()) {
			return cloneElement(trigger, {
				ref,
				onClick: toggleDropdown
			});
		} else {
			return <Button type={ButtonType.UTILITY} variant={ButtonVariant.UTILITY} onClick={toggleDropdown} text={trigger} ref={ref} fullWidth={fullWidth} />;
		}
	};

	const dropdownListClass = classNames('DS__dropdown-list', {
		'DS__dropdown-list--small': size === 'small'
	});

	const dropdownAllOptionClass = classNames('DS__dropdown-item__all-option', {
		'DS__dropdown-item__all-option--with-category': hasCategory
	});

	/* Popper content UI */
	const renderPopper = ({ ref, style, placement, arrowProps }) => (
		<ul
			className={dropdownListClass}
			ref={ref}
			style={{
				...style,
				width: setWidth ? `${setWidth}px` : dropdownWidth ? `${dropdownWidth}px` : 'auto',
				minWidth: '160px',
				// You can also force a higher z-index here if needed
				zIndex: 9999
			}}
			data-placement={placement}
		>
			{isLoading ? (
				<div className="DS__dropdown-list__loading-list">
					<Loader />
				</div>
			) : noResults ? (
				<div className="DS__dropdown-list__no-results-list">
					<div className="DS__dropdown-list__no-results-content">
						<div className="DS__dropdown-list__no-result-icon">
							<Icon name="information-circle" size="medium" style="outlined" />
						</div>
						<div className="DS__dropdown-list__no-result-text">No results found.</div>
					</div>
				</div>
			) : (
				<>
					{allowSelectAll && (
						<li className="DS__dropdown-item">
							<Button
								type={ButtonType.UTILITY}
								variant={ButtonVariant.UTILITY}
								onClick={handleSelectAll}
								text={
									<div className={dropdownAllOptionClass}>
										<div className="DS__dropdown-item__content-checkbox">
											<Checkbox id="select-all" checked={temporarySelection.length === reorderedOptions.length} onChange={handleSelectAll} />
										</div>
										<div className="DS__dropdown-item__all-option-text">All options</div>
									</div>
								}
							/>
						</li>
					)}
					{renderGroupedOptions()}
				</>
			)}
			<div ref={arrowProps.ref} style={arrowProps.style} />
		</ul>
	);

	/* Render the Popper.
	If portal={true}, wrap it with createPortal. Otherwise, render inline.
*/
	const popperContent = (
		<Popper placement={placement || 'bottom-start'} modifiers={[{ name: 'offset', options: { offset } }]}>
			{renderPopper}
		</Popper>
	);

	return (
		<Manager>
			<Reference>{renderReference}</Reference>
			{isOpen && !disabled && (portal ? createPortal(popperContent, document.body) : popperContent)}
		</Manager>
	);
};

export default withDropdownManager(Dropdown);

OptionItem.propTypes = {
	option: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired, // ID for the option, must be a string or number
		label: PropTypes.string.isRequired, // Label to display for the option
		subLabel: PropTypes.string, // Optional sub-label for the option
		isDisabled: PropTypes.bool // Boolean flag indicating if the option is disabled
	}).isRequired, // Object representing the option

	multiple: PropTypes.bool, // Boolean indicating if multiple options can be selected
	hasCategory: PropTypes.bool, // Boolean indicating if the option is grouped by category
	temporarySelection: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired // ID of the selected option
		})
	).isRequired, // Array of temporarily selected options

	handleMultipleSelection: PropTypes.func.isRequired, // Function to handle multiple selections
	handleSingleSelection: PropTypes.func.isRequired, // Function to handle single selection
	size: PropTypes.oneOf(['small', 'default']) // Size of the dropdown items, can be 'small' or 'default'
};

CategoryGroup.propTypes = {
	hasCategory: PropTypes.bool, // Boolean indicating if options are grouped by category
	category: PropTypes.string.isRequired, // Name of the category (e.g., "Category 1", "Category 2")
	groupedOptions: PropTypes.objectOf(
		PropTypes.arrayOf(
			PropTypes.shape({
				id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired, // ID for each option
				label: PropTypes.string.isRequired, // Label for each option
				subLabel: PropTypes.string, // Optional sub-label
				isDisabled: PropTypes.bool // Optional flag for disabled state
			})
		)
	).isRequired, // Grouped options by category
	selectByCategory: PropTypes.bool, // Boolean indicating if category-level selection is allowed
	temporarySelection: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired // ID of the selected option
		})
	).isRequired, // Array of temporarily selected options

	size: PropTypes.oneOf(['small', 'default']), // Size of the dropdown items, can be 'small' or 'default'
	handleSelectAllInCategory: PropTypes.func.isRequired, // Function to handle "select all" action within a category
	multiple: PropTypes.bool, // Boolean indicating if multiple options can be selected
	handleMultipleSelection: PropTypes.func.isRequired, // Function to handle multiple selections
	handleSingleSelection: PropTypes.func.isRequired // Function to handle single selection
};

Dropdown.propTypes = {
	options: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired, // Each option needs a unique id (string or number)
			label: PropTypes.string.isRequired, // Label to display for the option
			subLabel: PropTypes.string, // Optional sub-label for the option
			isDisabled: PropTypes.bool, // Optionally disable individual items
			category: PropTypes.string // Category to group options under, optional
		})
	).isRequired, // List of dropdown options
	filteredOptions: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired, // Each option needs a unique id (string or number)
			label: PropTypes.string.isRequired, // Label to display for the option
			subLabel: PropTypes.string, // Optional sub-label for the option
			isDisabled: PropTypes.bool, // Optionally disable individual items
			category: PropTypes.string // Category to group options under, optional
		})
	), // List of dropdown options
	value: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired // Selected value should also have an id
		})
	), // Current selected value(s)

	onAdd: PropTypes.func.isRequired, // Callback function when options are added/selected

	multiple: PropTypes.bool, // Whether the dropdown allows multiple selections
	allowSelectAll: PropTypes.bool, // Whether the dropdown has an option to select all
	selectByCategory: PropTypes.bool, // Whether the dropdown should allow selection by category
	trigger: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired, // Content or button that triggers the dropdown
	isOpen: PropTypes.bool.isRequired, // Whether the dropdown is open
	toggleDropdown: PropTypes.func.isRequired, // Function to toggle the dropdown state
	dropdownWidth: PropTypes.number, // The width of the dropdown
	setWidth: PropTypes.number, // Optional custom width for the dropdown
	isLoading: PropTypes.bool, // Show loading state while dropdown options are being loaded
	size: PropTypes.oneOf(['small', 'default']), // The size of the dropdown items
	noResults: PropTypes.bool, // Boolean flag indicating if there are no results
	placement: PropTypes.oneOf(['top', 'top-start', 'top-end', 'bottom', 'bottom-start', 'bottom-end', 'left', 'left-start', 'left-end', 'right', 'right-start', 'right-end']), // Placement of the dropdown
	offset: PropTypes.arrayOf(PropTypes.number), // Offset for the dropdown
	disabled: PropTypes.bool, // Boolean flag indicating if the dropdown is disabled
	portal: PropTypes.bool, // Boolean flag indicating if the dropdown should be rendered in a portal
	fullWidth: PropTypes.bool // Boolean flag indicating if the dropdown should be full width
};
